'use client'

import React, { useContext, useMemo } from "react";
import axios from "axios";
import {Content} from "@/types/api";
import { useAuth } from "@/contexts/AuthContext";
import { COGNITO_TOKEN_STORAGE_KEY } from "@/lib/cognitoAdminAuth";
import { loginUrlWithReturn } from "@/utils/authNavigation";

const BASE_URL = process.env.API_URL?.replace(/\/+$/, "");
const TABLE_ENDPOINTS: Record<string, string> = {
  content: "content",
  series: "series",
  rotation: "rotation",
  audio: "audio",
  "audio-relation": "audio-relation",
  users: "admin/users",
  subscriptions: "admin/subscriptions",
  profiles: "admin/profiles",
  "beta-users": "admin/beta-users",
  "privacy-requests": "admin/privacy-requests",
};

type Props = {
  children?: React.ReactNode;
};

const ApiContext = React.createContext<ReturnType<typeof useApiInternal> | undefined>(undefined);

const apiRequest = async <T,>(
  url: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  getAccessToken: () => Promise<string | null>,
  data?: any,
  params?: any
): Promise<T> => {
  try {
    const token = typeof window !== "undefined" ? await getAccessToken() : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const response = await axios({
      url,
      method,
      data,
      params,
      headers,
    });
    // console.log(response.data);
    return response.data;
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 401 && typeof window !== "undefined") {
      window.localStorage.removeItem("adminToken");
      window.localStorage.removeItem(COGNITO_TOKEN_STORAGE_KEY);
      const path = (window.location.pathname + window.location.search) || "/content";
      window.location.assign(loginUrlWithReturn(path));
    }
    console.error(error);
    throw error;
  }
};

function useApiInternal() {
  const { getAccessToken } = useAuth();

  return useMemo(() => {
    return {
      fetchContent: () => apiRequest<Content[]>(`${BASE_URL}/content`, "GET", getAccessToken),
      fetchSchema: (table: string) => apiRequest(`${BASE_URL}/admin/schema/${table}`, "GET", getAccessToken),
      createContent: (content: Content[]) => apiRequest<Content>(`${BASE_URL}/content`, "POST", getAccessToken, content),
      updateContent: (content: Content) => apiRequest<Content>(`${BASE_URL}/content`, "PUT", getAccessToken, content),
      deleteContent: (id: string) => apiRequest<Content>(`${BASE_URL}/content/${id}`, "DELETE", getAccessToken),
      fetchTable: (table: string) => {
        const endpoint = TABLE_ENDPOINTS[table];
        if (!endpoint) {
          return Promise.reject(new Error(`Unsupported table: ${table}`));
        }
        const params =
          table === "rotation" || table === "series"
            ? { limit: 200 }
            : undefined;
        return apiRequest<any>(`${BASE_URL}/${endpoint}`, "GET", getAccessToken, undefined, params).then((response) =>
          Array.isArray(response?.data) ? response.data : response,
        );
      },
      createTableItem: (table: string, item: Record<string, any>) => {
        const endpoint = TABLE_ENDPOINTS[table];
        if (!endpoint) {
          return Promise.reject(new Error(`Unsupported table: ${table}`));
        }
        const payload = ["content", "series", "rotation", "audio", "audio-relation"].includes(table)
          ? [item]
          : item;
        return apiRequest<Record<string, any>>(`${BASE_URL}/${endpoint}`, "POST", getAccessToken, payload);
      },
      updateTableItem: (table: string, item: Record<string, any>) => {
        const endpoint = TABLE_ENDPOINTS[table];
        if (!endpoint) {
          return Promise.reject(new Error(`Unsupported table: ${table}`));
        }
        return apiRequest<Record<string, any>>(`${BASE_URL}/${endpoint}`, "PUT", getAccessToken, item);
      },
      deleteTableItem: (table: string, id: string) => {
        const endpoint = TABLE_ENDPOINTS[table];
        if (!endpoint) {
          return Promise.reject(new Error(`Unsupported table: ${table}`));
        }
        return apiRequest<Record<string, any>>(`${BASE_URL}/${endpoint}/${id}`, "DELETE", getAccessToken);
      },
      previewAudioPlaylistImport: (payload: {
        playlistUrl: string;
        contentId: string;
        authorName?: string;
        maxTracks?: number;
        skipExistingByName?: boolean;
        s3FolderPrefix?: string;
      }) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/audio/import-playlist`, "POST", getAccessToken, {
          ...payload,
          dryRun: true,
        }),
      runAudioPlaylistImport: (payload: {
        playlistUrl: string;
        contentId: string;
        authorName?: string;
        maxTracks?: number;
        skipExistingByName?: boolean;
        s3FolderPrefix?: string;
      }) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/audio/import-playlist`, "POST", getAccessToken, {
          ...payload,
          dryRun: false,
        }),
      fetchAiConfig: () => apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/config`, "GET", getAccessToken),
      updateAiConfig: (payload: Record<string, any>) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/config`, "PUT", getAccessToken, payload),
      fetchAiUsage: () => apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/usage`, "GET", getAccessToken),
      fetchAiReviewQueue: () => apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/review`, "GET", getAccessToken),
      approveAiReview: (cacheId: string) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/review/approve`, "POST", getAccessToken, { cacheId }),
      rejectAiReview: (cacheId: string) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/review/reject`, "POST", getAccessToken, { cacheId }),
      regenerateAiReview: (cacheId: string) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/review/regenerate`, "POST", getAccessToken, { cacheId }),
      fetchAiIngestionStatus: () =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/ingestion/status`, "GET", getAccessToken),
      triggerAiIngestion: () =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/ingestion/trigger`, "POST", getAccessToken, {}),
      updateAiContentExclusion: (sourceKey: string, excluded: boolean) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/content/exclusion`, "PUT", getAccessToken, {
          sourceKey,
          excluded,
        }),
      fetchAiCostProjection: () =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/cost-projection`, "GET", getAccessToken),
      sendAiChat: (payload: { message: string; bookId?: string }) =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/chat`, "POST", getAccessToken, payload),
      fetchAiChatHistory: () =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/chat/history`, "GET", getAccessToken),
      clearAiChatHistory: () =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/ai/chat/history`, "DELETE", getAccessToken),
      fetchAuthMigrationStatus: () =>
        apiRequest<Record<string, any>>(`${BASE_URL}/admin/auth/migration-status`, "GET", getAccessToken),
    };
  }, [getAccessToken]);
}

export const ApiProvider = ({ children }: Props) => {
  const api = useApiInternal();

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const api = useContext(ApiContext);

  if (!api) {
    throw new Error("Authenticated API used outside of ApiContextProvider");
  }

  return api;
};

export default ApiProvider;
